import React from 'react';
import { Box, Paper, Typography, useTheme } from '@mui/material';

// One text size for all card text in every Reports tab; emphasis is by weight only.
const CARD_FONT_SIZE = '0.8125rem'; // 13px

// Ledger-style flat card shared by all Reports tabs on phones. Lines wrap rather
// than clip, so a money value never loses a digit.
const ReportsMobileCard = ({ lines, footer, footerAction, footerActionInline = false, footerRight }) => {
  const theme = useTheme();
  const lineSx = { display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', justifyContent: 'space-between', columnGap: 1 };

  const renderRight = (right) => {
    if (!right) return null;
    const labelText = right.label && `${right.label}: `;
    if (right.strong) {
      return (
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: CARD_FONT_SIZE,
            whiteSpace: 'nowrap',
            flexShrink: 0,
            ml: 'auto',
            color: right.color || 'inherit',
          }}
        >
          {labelText && (
            <Typography component="span" sx={{ fontSize: CARD_FONT_SIZE, fontWeight: 400, color: 'text.secondary' }}>
              {labelText}
            </Typography>
          )}
          {right.value}
        </Typography>
      );
    }
    return (
      <Typography
        sx={{ fontSize: CARD_FONT_SIZE, color: 'text.secondary', whiteSpace: 'nowrap', flexShrink: 0, ml: 'auto' }}
      >
        {labelText}
        <Typography component="span" sx={{ fontSize: CARD_FONT_SIZE, fontWeight: 500, color: right.color || 'text.primary' }}>
          {right.value}
        </Typography>
      </Typography>
    );
  };

  const footerRowSx = {
    px: 1.5,
    py: 0.75,
    borderTop: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
  };
  const footerText = (
    <Typography
      sx={{
        fontSize: CARD_FONT_SIZE,
        color: 'text.secondary',
        minWidth: 0,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {footer}
    </Typography>
  );

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 1,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ px: 1.5, py: 1 }}>
        {lines.map((line, index) => (
          <Box key={index} sx={index === 0 ? lineSx : { ...lineSx, mt: 0.25 }}>
            {line.left !== undefined && (
              <Typography
                sx={index === 0
                  ? { fontWeight: 600, fontSize: CARD_FONT_SIZE, whiteSpace: 'nowrap' }
                  : { fontSize: CARD_FONT_SIZE, color: 'text.secondary', whiteSpace: 'nowrap' }}
              >
                {line.left}
              </Typography>
            )}
            {renderRight(line.right)}
          </Box>
        ))}
      </Box>

      {(footer || footerAction) && (footerActionInline ? (
        // Text + action travel together on the left; footerRight sits at the
        // end and wraps to its own line when the row is too narrow for both.
        <Box
          sx={{
            ...footerRowSx,
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            columnGap: 1,
            rowGap: 0.25,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, maxWidth: '100%' }}>
            {footerText}
            {footerAction && <Box sx={{ display: 'flex', flexShrink: 0 }}>{footerAction}</Box>}
          </Box>
          {renderRight(footerRight)}
        </Box>
      ) : (
        <Box sx={{ ...footerRowSx, justifyContent: 'space-between', gap: 1 }}>
          {footerText}
          {footerAction && <Box sx={{ display: 'flex', flexShrink: 0 }}>{footerAction}</Box>}
        </Box>
      ))}
    </Paper>
  );
};

export default ReportsMobileCard;
